/**
 * Utilitários para processamento de URLs de avatar
 */

export interface AvatarUrlOptions {
  addTimestamp?: boolean;
  /** Se informado, usa este valor em vez de Date.now() para cache-bust (ex.: refresh a cada 5 min) */
  timestamp?: number;
  crossOrigin?: boolean;
}

/**
 * Processa URL de avatar baseado no tipo de URL
 * @param avatarUrl URL original do avatar
 * @param options Opções de processamento
 * @returns URL processada ou null se inválida
 */
export const processAvatarUrl = (avatarUrl: string | null, options: AvatarUrlOptions = {}): string | null => {
  if (!avatarUrl || avatarUrl.trim() === '') {
    return null;
  }

  const { addTimestamp = true, timestamp } = options;

  if (avatarUrl.startsWith('data:image')) {
    return avatarUrl;
  }

  // URLs públicas (MinIO) — adicionar cache-bust se solicitado
  if (addTimestamp) {
    const separator = avatarUrl.includes('?') ? '&' : '?';
    const t = timestamp !== undefined ? timestamp : new Date().getTime();
    return `${avatarUrl}${separator}t=${t}`;
  }

  return avatarUrl;
};

/**
 * @deprecated URLs legadas pré-assinadas; MinIO usa URLs públicas diretas.
 */
export const isSupabaseSignedUrl = (url: string): boolean => false;

/**
 * Verifica se uma URL de avatar ainda é válida (não expirada)
 * @param url URL para verificar
 * @returns Promise<boolean> indicando se a URL é válida
 */
export const validateAvatarUrl = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { 
      method: 'HEAD',
      mode: 'cors'
    });
    return response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Gera initials para fallback do avatar
 * @param name Nome da pessoa
 * @returns String com as iniciais
 */
export const generateInitials = (name: string): string => {
  if (!name || name.trim() === '') {
    return '?';
  }

  return name
    .trim()
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Hook para lidar com erro de carregamento de avatar
 * @param avatarUrl URL do avatar
 * @param onError Callback chamado quando há erro
 * @returns Função para ser usada no onError da imagem
 */
export const createAvatarErrorHandler = (
  avatarUrl: string,
  onError?: (error: Event) => void
) => {
  return (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Erro ao carregar avatar:', {
      src: avatarUrl,
      error: e,
      naturalWidth: e.currentTarget.naturalWidth,
      naturalHeight: e.currentTarget.naturalHeight
    });

    const target = e.currentTarget;
    target.onerror = null;

    if (!avatarUrl.includes('retry=1')) {
      const retryUrl = avatarUrl.includes('?')
        ? `${avatarUrl}&retry=1`
        : `${avatarUrl}?retry=1`;
      target.src = retryUrl;
    } else {
      target.style.display = 'none';
    }

    // Chamar callback de erro personalizado
    if (onError) {
      onError(e.nativeEvent);
    }
  };
};

/**
 * Configurações padrão para elementos img de avatar
 */
export const getAvatarImageProps = (
  avatarUrl: string,
  alt: string,
  onError?: (error: Event) => void
) => {
  return {
    src: processAvatarUrl(avatarUrl),
    alt,
    crossOrigin: 'anonymous' as const,
    loading: 'eager' as const,
    onError: createAvatarErrorHandler(avatarUrl, onError)
  };
}; 